import { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { shouldApplyBulk } from '../utils/complianceFilters';

/**
 * useBulkReviewerDecisions
 *
 * Fire-and-forget bulk-verdict apply for the reviewer campaign page.
 * Returns `{ apply, isBulkInFlight }` — the toolbar IconButton uses the
 * latter to prevent overlapping bulk runs while one is still flushing.
 *
 * Apply pipeline (one user click → one optimistic flip → background
 * sequential POSTs):
 *
 *   1. Read the current `useReviewerCampaign` cache snapshot.
 *   2. Compute the FILTER SET: every (contact_membership_id, config_id)
 *      pair that is bulk-eligible per `shouldApplyBulk` (see
 *      `utils/complianceFilters.js` for the full predicate). Per-cell
 *      manual flags (any verdict) are sacred and never overwritten;
 *      bulk-applied flags ARE overwritable so bulk runs stay reversible.
 *   3. ONE `setQueryData` flips every cell in the filter set to the
 *      optimistic blob in a single render. The virtualized table paints
 *      its final state immediately; skipped cells never blink.
 *   4. Chunk the filter set into batches of `BULK_BATCH_SIZE = 400`
 *      (under the server's 500-decision cap) and POST sequentially.
 *      Sequential keeps server load predictable, makes the result toast
 *      deterministic, and stays comfortably under the per-user
 *      240 req/60s rate limit even at worst-case 5,000-cell campaigns.
 *   5. Per-batch reconciliation: replace the optimistic blob with the
 *      server's authoritative `compliance` for each `ok` cell; roll back
 *      just the cell on `ok: false` and add it to the failures list.
 *   6. Final-batch settle: one `invalidateQueries` for consistency, then
 *      surface a single result toast with the totals.
 *
 * Concurrency contract:
 *   - No AbortController. Modal closes on Apply, work is fire-and-forget.
 *     Pending writes match the user's stated intent; we let them land.
 *   - The toolbar disables the bulk trigger while `isBulkInFlight === true`
 *     so two overlapping bulk runs cannot stomp each other's reconciliation.
 *
 * `expected_content_hash` is intentionally omitted from the payload.
 * Bulk semantics: "trust the current content"; sending a stale hash
 * would TOCTOU-reject every cell the advisor mutated since page load,
 * which is the opposite of what the reviewer asked for. Single-cell
 * popovers continue to send the hash because there the reviewer is
 * staring at the exact text they're signing for.
 */

const BULK_BATCH_SIZE = 1000;

const buildOptimisticBlob = ({ verdict, note, userId, now }) => ({
  status: verdict,
  note: note ?? null,
  decided_by: 'human',
  // Always true on bulk writes — drives the reversibility rule in
  // `shouldApplyBulk` so a subsequent bulk can flip these cells.
  bulk_applied: true,
  reviewer_user_id: userId || null,
  reviewer_display_name: null,
  updated_at: now,
  is_stale: false,
});

/**
 * In-place merge of decision blobs into a contacts-array snapshot. Used
 * for both the up-front optimistic flip and per-batch reconciliation.
 *
 * `decisionMap` is a Map<string, object|null> keyed on
 * `${contactMembershipId}::${configId}`. A `null` value rolls the cell
 * back to its pre-bulk compliance (looked up in `rollbackMap`).
 */
const mergeDecisionsIntoContacts = (contacts, decisionMap, rollbackMap) =>
  contacts.map((row) => {
    const cmid = row.contact_membership_id;
    if (!cmid) return row;

    let nextDrafts = row.drafts;
    let touched = false;

    for (const [key, blob] of decisionMap.entries()) {
      const [keyCmid, keyCfg] = key.split('::');
      if (keyCmid !== cmid) continue;

      if (!touched) {
        nextDrafts = { ...(row.drafts || {}) };
        touched = true;
      }
      const cell = nextDrafts[keyCfg] || {};
      const compliance = blob === null ? rollbackMap.get(key) ?? null : blob;
      nextDrafts[keyCfg] = { ...cell, compliance };
    }

    return touched ? { ...row, drafts: nextDrafts } : row;
  });

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

export function useBulkReviewerDecisions(campaignId) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isBulkInFlight, setIsBulkInFlight] = useState(false);
  // Ref-mirror so the apply closure can guard its own re-entry without
  // racing setState (apply is also exposed and could in theory be called
  // twice in the same tick before React re-renders).
  const inFlightRef = useRef(false);

  const queryKey = useMemo(
    () =>
      user?.id && campaignId
        ? ['reviewerCampaign', user.id, campaignId]
        : null,
    [user?.id, campaignId],
  );

  const apply = useCallback(
    async ({ verdict, note, configIds }) => {
      if (!queryKey) {
        return { applied: 0, skipped: 0, failed: 0, failures: [] };
      }
      if (inFlightRef.current) {
        return { applied: 0, skipped: 0, failed: 0, failures: [] };
      }

      const snapshot = queryClient.getQueryData(queryKey);
      if (!snapshot || !Array.isArray(snapshot.contacts)) {
        return { applied: 0, skipped: 0, failed: 0, failures: [] };
      }

      const configIdSet = new Set(configIds || []);
      if (configIdSet.size === 0) {
        return { applied: 0, skipped: 0, failed: 0, failures: [] };
      }

      // Build the filter + skip sets in one pass. Decisions are flat
      // little tuples; we keep the original `compliance` per cell on the
      // side so per-cell rollback can restore it byte-identically.
      const decisions = [];
      const rollbackMap = new Map(); // key → previous compliance (null if none)
      let skipped = 0;

      for (const row of snapshot.contacts) {
        const cmid = row.contact_membership_id;
        if (!cmid) continue;
        for (const cfg of configIdSet) {
          const cell = row.drafts?.[cfg];
          // Cell missing (advisor hasn't generated this draft for this
          // contact) — nothing to verdict against, skip silently and
          // don't count toward the skip bucket either; it's not a cell
          // the reviewer expects to see in the preview.
          if (!cell) continue;
          const compliance = cell.compliance || null;
          if (!shouldApplyBulk(compliance, verdict)) {
            skipped += 1;
            continue;
          }
          decisions.push({ cmid, cfg });
          rollbackMap.set(`${cmid}::${cfg}`, compliance);
        }
      }

      if (decisions.length === 0) {
        return { applied: 0, skipped, failed: 0, failures: [] };
      }

      // ── Step 3: one-shot optimistic merge ──────────────────────────
      const now = new Date().toISOString();
      const optimisticBlob = buildOptimisticBlob({
        verdict,
        note: note ?? null,
        userId: user?.id,
        now,
      });
      const optimisticMap = new Map(
        decisions.map(({ cmid, cfg }) => [
          `${cmid}::${cfg}`,
          optimisticBlob,
        ]),
      );

      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData(queryKey, (current) => {
        if (!current) return current;
        return {
          ...current,
          contacts: mergeDecisionsIntoContacts(
            current.contacts || [],
            optimisticMap,
            rollbackMap,
          ),
        };
      });

      // ── Step 4–5: sequential batches with per-cell reconciliation ──
      inFlightRef.current = true;
      setIsBulkInFlight(true);

      let applied = 0;
      let failed = 0;
      const failures = [];

      try {
        const batches = chunk(decisions, BULK_BATCH_SIZE);
        for (const batch of batches) {
          const requestBody = {
            decisions: batch.map(({ cmid, cfg }) => ({
              contact_membership_id: cmid,
              config_id: cfg,
              status: verdict,
              note: note ?? null,
              expected_content_hash: null,
              // Provenance flag — keeps these decisions reversible by a
              // subsequent bulk run. Per-cell popover writes leave this
              // false (default on the server-side Pydantic model).
              bulk_applied: true,
            })),
          };

          let results = [];
          try {
            const { data } = await api.post(
              `/api/reviews/campaigns/${campaignId}/decisions`,
              requestBody,
            );
            results = Array.isArray(data?.results) ? data.results : [];
          } catch (err) {
            // Whole-batch failure — server unreachable, 4xx, etc. Roll
            // every cell in this batch back. Log once per batch; don't
            // spam per-cell errors when the network is the cause.
            // eslint-disable-next-line no-console
            console.error('Bulk decisions batch failed', err);
            const rollbackBatch = new Map(
              batch.map(({ cmid, cfg }) => [`${cmid}::${cfg}`, null]),
            );
            queryClient.setQueryData(queryKey, (current) => {
              if (!current) return current;
              return {
                ...current,
                contacts: mergeDecisionsIntoContacts(
                  current.contacts || [],
                  rollbackBatch,
                  rollbackMap,
                ),
              };
            });
            failed += batch.length;
            for (const { cmid, cfg } of batch) {
              failures.push({
                contact_membership_id: cmid,
                config_id: cfg,
                error_code: 'network_error',
                error_message:
                  err?.response?.data?.detail || 'Could not save decision.',
              });
            }
            continue;
          }

          // Per-cell reconciliation. Build a map of authoritative blobs
          // (replace optimistic) for `ok` cells and `null` rollbacks for
          // `ok: false` cells, then merge in one cache write per batch.
          const reconcileMap = new Map();
          for (const result of results) {
            const key = `${result.contact_membership_id}::${result.config_id}`;
            if (result.ok) {
              reconcileMap.set(key, result.compliance || optimisticBlob);
              applied += 1;
            } else {
              reconcileMap.set(key, null);
              failed += 1;
              failures.push({
                contact_membership_id: result.contact_membership_id,
                config_id: result.config_id,
                error_code: result.error_code || 'unknown',
                error_message:
                  result.error_message || 'Could not save decision.',
              });
            }
          }

          if (reconcileMap.size > 0) {
            queryClient.setQueryData(queryKey, (current) => {
              if (!current) return current;
              return {
                ...current,
                contacts: mergeDecisionsIntoContacts(
                  current.contacts || [],
                  reconcileMap,
                  rollbackMap,
                ),
              };
            });
          }
        }
      } finally {
        inFlightRef.current = false;
        setIsBulkInFlight(false);
        // Belt-and-suspenders: a clean refetch after the chain reconciles
        // any cell the server may have stamped with extra metadata we
        // don't model client-side (e.g. server-computed `is_stale` after
        // a parallel advisor edit landed mid-bulk).
        queryClient.invalidateQueries({ queryKey });
      }

      return { applied, skipped, failed, failures };
    },
    [campaignId, queryClient, queryKey, user?.id],
  );

  return { apply, isBulkInFlight };
}
